// TrendingSection.js
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ProductCard from './ProductCard';
import api from '../utils/api';
import { fetchAfterHero, registerShownProducts } from '../utils/feedRegistry';
import '../StylesComponents/trending-section.css';

function TrendingSection({ currentUser }) {
  const [liveProducts, setLiveProducts] = useState([]);
  const scrollRef = useRef(null);
  const containerRef = useRef(null);
  const canvasRef = useRef(null);

  const isDraggingState = useRef(false);
  const [isDragging, setIsDragging] = useState(false); 
  
  const startX = useRef(0);
  const scrollLeftStart = useRef(0);
  const velocity = useRef(0);
  const lastX = useRef(0);
  const scrollPos = useRef(0);
  const isSelectorActive = useRef(false);
  
  const animationFrameId = useRef(null);
  const dragThreshold = 5; 
  const autoScrollSpeed = 0.4; 
  const momentumFriction = 0.97; 

  useEffect(() => {
    const fetchTrending = async () => {
      try {
        const res = await fetchAfterHero((exclude) => api.get('/api/products', {
          params: {
            trending: 'true',
            limit: 20,
            feedContext: 'trending',
            discountRanges: '10+',
            ...(exclude.length > 0 ? { exclude: exclude.join(',') } : {}),
          },
        }));
        let items = res.data.products || [];
        registerShownProducts(items);
        // Two copies for seamless infinite scroll (was 3 — cuts DOM image nodes by ~1/3)
        setLiveProducts([...items, ...items]);
      } catch (e) {
        console.error('Trending fetch error:', e);
      }
    };
    fetchTrending();
  }, []);

  useEffect(() => {
    if (liveProducts.length === 0) return;
    const slider = scrollRef.current;
    if (!slider) return;

    if (scrollPos.current === 0) {
      const init = () => {
        if (slider) {
          const startPos = slider.scrollWidth / 2;
          scrollPos.current = startPos;
          slider.scrollLeft = startPos;
        }
      };
      setTimeout(init, 200);
    }

    const update = () => {
      if (slider) {
        if (!isDraggingState.current && !isSelectorActive.current) {
          scrollPos.current += autoScrollSpeed + velocity.current;
          velocity.current *= momentumFriction;
          const oneHalf = slider.scrollWidth / 2;
          if (scrollPos.current >= oneHalf) {
            scrollPos.current -= oneHalf;
          } else if (scrollPos.current <= 0) {
            scrollPos.current += oneHalf;
          }
          slider.scrollLeft = scrollPos.current;
        } else if (isDraggingState.current) {
          scrollPos.current = slider.scrollLeft;
        }
      }
      animationFrameId.current = requestAnimationFrame(update);
    };

    animationFrameId.current = requestAnimationFrame(update);
    return () => cancelAnimationFrame(animationFrameId.current);
  }, [liveProducts]);

  // Canvas Background (Grid)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let frame;
    const resize = () => {
      if (canvas && canvas.parentElement) {
        canvas.width = window.innerWidth;
        canvas.height = canvas.parentElement.offsetHeight;
      }
    };
    window.addEventListener('resize', resize);
    resize();
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = 'rgba(0, 230, 118, 0.08)';
      ctx.lineWidth = 1.5;
      const step = 80;
      const offset = (Date.now() / 40) % step;
      for(let x=0; x<canvas.width; x+=step) { ctx.beginPath(); ctx.moveTo(x,0); ctx.lineTo(x,canvas.height); ctx.stroke(); }
      for(let y=offset; y<canvas.height; y+=step) { ctx.beginPath(); ctx.moveTo(0,y); ctx.lineTo(canvas.width,y); ctx.stroke(); }
      frame = requestAnimationFrame(draw);
    };
    draw();
    return () => { window.removeEventListener('resize', resize); cancelAnimationFrame(frame); };
  }, []);

  const hasMoved = useRef(false);

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    
    isDraggingState.current = true;
    hasMoved.current = false;
    startX.current = e.pageX - scrollRef.current.offsetLeft;
    scrollLeftStart.current = scrollRef.current.scrollLeft;
    velocity.current = 0;
    lastX.current = e.pageX;

    window.addEventListener('mouseup', stopDragging);
  };

  const handleMouseMove = (e) => {
    if (!isDraggingState.current) return;
    const x = e.pageX - scrollRef.current.offsetLeft;
    if (Math.abs(x - startX.current) > dragThreshold) {
      setIsDragging(true);
      hasMoved.current = true;
      document.body.style.userSelect = 'none';
    }
    velocity.current = (lastX.current - e.pageX) * 0.8;
    lastX.current = e.pageX;
    const walk = (x - startX.current) * 1.2;
    scrollRef.current.scrollLeft = scrollLeftStart.current - walk;
  };

  const stopDragging = (e) => {
    if (hasMoved.current) {
      // Create a temporary capturing listener to swallow the subsequent click event
      const captureClick = (clickEvent) => {
        clickEvent.stopPropagation();
        clickEvent.preventDefault();
        window.removeEventListener('click', captureClick, true);
      };
      window.addEventListener('click', captureClick, true);
    }

    isDraggingState.current = false;
    document.body.style.userSelect = '';
    window.removeEventListener('mouseup', stopDragging);
    
    // Use a small delay to ensure any pending logic sees the final state
    setTimeout(() => {
      setIsDragging(false);
      hasMoved.current = false;
    }, 10);
  };

  const handleTouchStart = (e) => {
    isDraggingState.current = true;
    startX.current = e.touches[0].pageX - scrollRef.current.offsetLeft;
    scrollLeftStart.current = scrollRef.current.scrollLeft;
    velocity.current = 0;
    lastX.current = e.touches[0].pageX;
  };

  const handleTouchMove = (e) => {
    if (!isDraggingState.current) return;
    const x = e.touches[0].pageX - scrollRef.current.offsetLeft;
    if (Math.abs(x - startX.current) > dragThreshold) {
      setIsDragging(true);
    }
    velocity.current = (lastX.current - x) * 0.8;
    lastX.current = x;
    const walk = (x - startX.current) * 1.2;
    scrollRef.current.scrollLeft = scrollLeftStart.current - walk;
  };

  return (
    <div className="trending-section-container" ref={containerRef}>
      <canvas ref={canvasRef} className="section-grid-canvas"></canvas>
      <div className="trending-heading-wrapper">
        <div className="trending-heading-row">
          <h2 className="trending-heading">TRENDING NOW</h2>
          <Link to="/trending" className="trending-view-all">
            VIEW ALL TRENDING →
          </Link>
        </div>
      </div>
      
      <div 
        className={`trending-carousel manual-control ${isDragging ? 'active' : ''}`}
        ref={scrollRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={stopDragging}
        onMouseLeave={() => { if (isDraggingState.current) stopDragging(); }}
        onDragStart={(e) => e.preventDefault()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={stopDragging}
      >
        <div className="trending-carousel-track">
          {liveProducts.map((product, index) => (
            <div 
              key={index} 
              className="trending-card-item"
              onClick={(e) => { if (isDragging) e.preventDefault(); }}
              style={{ flex: '0 0 auto', width: '300px' }}
            >
              <ProductCard 
                product={product} 
                currentUser={currentUser}
                theme="dark"
                onSizeSelectorToggle={(isActive) => { isSelectorActive.current = isActive; }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default TrendingSection;
